import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLES = ["all", "coach", "parent", "org", "athlete"];

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([api.get(`/admin/users?role=${filter}`), api.get("/organizations")]);
      setUsers(u.data); setOrgs(o.data);
    } catch { toast.error("Failed to load users."); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/users/${id}`, { status }); toast.success(`User ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setUserOrg = async (id, organization_id) => {
    try { await api.patch(`/admin/users/${id}`, { organization_id: organization_id || null }); toast.success("Organization updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">Users</h1>
      <div className="flex gap-2 flex-wrap mb-6">
        {ROLES.map((r) => (
          <button key={r} data-testid={`users-filter-${r}`} onClick={() => setFilter(r)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${filter === r ? "bg-white border-white text-black" : "border-white/20 text-white/60 hover:text-white"}`}>{r}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div> : users.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="users-empty">No users found.</div>
      ) : (
        <div className="space-y-3" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-${u.id}`} className="bg-[#121212] border border-white/10 p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-bold">{u.name || "—"} <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/20 px-2 py-0.5 ml-2">{u.role}</span></p>
                <p className="text-white/40 text-sm mt-0.5">{u.email}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {u.role === "coach" && (
                  <select data-testid={`user-org-${u.id}`} value={u.organization_id || ""} onChange={(e) => setUserOrg(u.id, e.target.value)} className="bg-[#0A0A0A] border border-white/15 text-xs px-3 py-2 outline-none focus:border-white">
                    <option value="">No org</option>
                    {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                )}
                <span className={`text-xs uppercase tracking-widest ${u.status === "active" ? "text-white" : "text-white/40"}`}>{u.status}</span>
                {u.status === "active" ? (
                  <button data-testid={`user-deactivate-${u.id}`} onClick={() => setStatus(u.id, "inactive")} className="border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Deactivate</button>
                ) : (
                  <button data-testid={`user-activate-${u.id}`} onClick={() => setStatus(u.id, "active")} className="border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Activate</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
